import { useFocusEffect } from '@react-navigation/native';
import { useSelectedProfile } from 'hooks/useSelectedProfile';
import { RootStackScreenProps } from 'navigation/AppNavigator';
import { useCallback, useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { useGetProgramsQuery, useGetTagQuery } from 'services/cms';
import theme from 'theme';
import { CmsCard } from 'types';
import { filterProgramsForAge } from 'utils/ageRating';
import { calcSizeForDevice, calcSizeForTv } from 'utils/index';
import { Platform } from 'utils/platform';
import { capitalize } from 'utils/string';

import EmptyState from 'components/EmptyState';
import FocusView from 'components/FocusView';
import Section from 'components/Section';
import IsLoading from 'components/isLoading';

const CategoryScreen: React.FC<RootStackScreenProps<'Category'>> = ({ navigation, route }) => {
  const analytics = useAnalytics();
  const { data: tag, isLoading: isLoadingTag } = useGetTagQuery(route.params.slug);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<any[]>([]);
  const [hasResolvedPrograms, setHasResolvedPrograms] = useState(false);

  const { selectedProfile } = useSelectedProfile();

  const { data: programsData, isLoading: isLoadingPrograms } = useGetProgramsQuery(
    { [`${tag?.type}._id`]: tag?._id, page },
    { skip: !tag }
  );

  const items: CmsCard[] = data.map((item) => ({
    link: `/${item.url}`,
    title: item.title,
    image: item.imageVertical,
    height: item.imageVertical.height,
    type: item.type,
  }));

  useEffect(() => {
    setPage(1);
    setData([]);
    setHasResolvedPrograms(false);
  }, [route.params.slug]);

  useEffect(() => {
    if (!tag || isLoadingPrograms || !programsData) return;

    if (programsData.items?.length) {
      const filteredData = filterProgramsForAge(programsData, selectedProfile?.ageRating ?? '18');

      setData((old) => {
        if (page === 1) {
          return filteredData.items;
        }

        const existingUrls = new Set(old.map((item) => item.url));
        const newItems = filteredData.items.filter((item) => !existingUrls.has(item.url));

        return [...old, ...newItems];
      });
    } else if (page === 1) {
      setData([]);
    }

    setHasResolvedPrograms(true);
  }, [tag, programsData, isLoadingPrograms, selectedProfile?.ageRating, page]);

  useEffect(() => {
    if (tag) navigation.setOptions({ title: tag.name });
  }, [navigation, tag]);

  useFocusEffect(
    useCallback(() => {
      if (tag) {
        analytics.triggerCategoryScreenPageViewEvent({
          categoryId: tag.id!,
          categoryName: tag.name!,
        });
      }
    }, [analytics, tag])
  );

  if (isLoadingTag || (isLoadingPrograms && data.length === 0)) {
    return (
      <View style={styles.loadingContainer}>
        <IsLoading />
      </View>
    );
  }

  if (!tag) {
    return (
      <View style={styles.container}>
        <Text style={styles.notFoundText}>Nenhum item encontrado</Text>
      </View>
    );
  }

  return (
    <FocusView trapFocusUp trapFocusRight style={{ flex: 1 }}>
      <View style={styles.contentContainer}>
        {(Platform.OS === 'web' || Platform.isTV) && (
          <Text style={styles.titleText}>{capitalize(tag.name || '')}</Text>
        )}
        {items?.length > 0 ? (
          <Section
            requestTVFocus
            onEndReached={() => setPage((prev) => prev + 1)}
            vertical
            items={items}
            _name="section"
            showTitle={false}
            title={tag?.name || ''}
            variant="section.medium"
            {...(Platform.isTV && { topContainerHeight: calcSizeForTv(240) })}
          />
        ) : hasResolvedPrograms ? (
          <EmptyState />
        ) : null}
      </View>
    </FocusView>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    flex: 1,
  },
  notFoundText: {
    color: '#fff',
    marginLeft: calcSizeForDevice(32),
    fontSize: calcSizeForDevice(24),
    fontWeight: '600',
  },
  contentContainer: {
    paddingHorizontal: calcSizeForDevice(32),
    paddingBottom: calcSizeForDevice(32, 0),
    paddingTop: calcSizeForDevice(20, 120),
  },
  titleText: {
    fontSize: calcSizeForDevice(30, 48),
    color: '#fff',
    lineHeight: calcSizeForDevice(30, 60),
    fontFamily: theme.fonts.inter.bold,
    marginLeft: calcSizeForDevice(0, 15),
    marginBottom: calcSizeForDevice(60),
  },
});

export default CategoryScreen;
